import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { JourneyViewLayout } from '@/features/journey/admin/templates/JourneyViewLayout';
import { useJourneyNavigate } from '@/administration/pages/Journey/utils/useJourneyNavigate';
import { useLearningJourneySession } from '@/administration/pages/Journey/store/journeySession/useLearningJourneySession';
import { JourneyViewShowDescriptionModal } from '@/features/journey/admin/atoms/JourneyViewShowDescriptionModal';
import { useUserContentLastParticipation } from '@/store/v2/catalog-v2/use-user-last-participation';
import { useCurrentUser } from '@/store/currentUser';
import { useLearningJourney } from '@/store/v2/journeys/useLearningJourney';
import {
  RLearningContentLocationData,
  useJoinLearningJourneyCohortsMutationRemote,
  useLearningJourneySectionQueryRemote,
  useLearningJourneyUpcomingEventLocationQueryRemote,
  useLearningJourneyUpcomingItemQueryRemote,
} from '@/store/v2';
import { useFavouriteJourney } from '@/administration/pages/Journey/store/favouriteJourney/useFavouriteJourney';
import { LearningCardError } from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCard';
import { learningContentDisplayMeta } from '@/store/v2/catalog-v2/model/learning-content';
import { slugify } from '@/utils/misc';
import { convertAvailabilityScheduledAtWithTimezone } from '@/administration/pages/Journey/common/utils/convertAvailabilityScheduledAtWithTimezone';
import { JourneyJoinModal } from '@/features/journey/admin/atoms/JourneyJoinModal';
import { richTextJsonToString } from '@/features/journey/admin/utils/richTextValue';
import { ROUTE_PATH } from '@/constants/routePaths';

const getAssignedSessionLocations = (
  locations: RLearningContentLocationData[] | undefined | null,
  locationsWithEnrollment:
    | Array<{
        id?: number;
        userLocationEnrollment?: Array<{ id?: number }> | undefined;
      }>
    | undefined,
): RLearningContentLocationData[] | undefined => {
  if (!locations?.length || !locationsWithEnrollment?.length) return locations ?? undefined;

  const assignedLocationId = locationsWithEnrollment.find(
    (location) => Array.isArray(location?.userLocationEnrollment) && location.userLocationEnrollment.length > 0,
  )?.id;

  if (typeof assignedLocationId !== 'number') return locations;

  const assignedLocation = locations.find((location) => Number(location?.id) === assignedLocationId);

  return assignedLocation ? [assignedLocation] : locations;
};

enum ModalType {
  ShowDescriptionModal = 'ShowDescriptionModal',
  JoinJourneyModal = 'JoinJourneyModal',
}

type ModalProps = {
  [ModalType.ShowDescriptionModal]: React.ComponentProps<typeof JourneyViewShowDescriptionModal>;
  [ModalType.JoinJourneyModal]: React.ComponentProps<typeof JourneyViewShowDescriptionModal>;
};

type Modal = keyof ModalProps;

type JourneyViewProps = {
  isInPreviewModal?: boolean;
};

export const JourneyView = ({ isInPreviewModal }: JourneyViewProps): JSX.Element | null => {
  const { user, loading: userLoading } = useCurrentUser();
  const { journeyId, sectionId } = useParams<{ journeyId: string; sectionId: string }>();
  const { navigate404 } = useJourneyNavigate();
  const navigate = useNavigate();

  const [activeModal, setModal] = useState<Modal | null>();

  const {
    journey,
    items,
    loading: journeyLoading,
    error: journeyError,
  } = useLearningJourney({
    id: Number(journeyId),
    include: {
      items: true,
      progress: true,
    },
    fetchPolicy: 'network-only',
    itemsFilter: user?.realm
      ? {
          realmVisibility: user.realm,
        }
      : undefined,
  });

  const { data: learningJourneySection, loading: learningJourneySectionLoading } = useLearningJourneySectionQueryRemote({
    variables: {
      id: Number(sectionId),
      filter: {
        realmVisibility: user?.realm,
      },
      pagination: {
        limit: -1,
        page: 1,
      },
      userId: user?.id ?? 0,
      spaceIds: [journey?.spaceId ?? 0],
      includeContentProgress: true,
      includeSectionProgress: false,
      includeUpdateByUser: false,
      includeIsLocked: true,
    },
    skip: !sectionId || !journeyId || !user?.id || !journey?.spaceId,
    fetchPolicy: 'network-only',
  });

  // The Journey progress widget's "up next" must always reflect the learner's next item
  // across the whole journey, independent of which section is currently being viewed.
  // We therefore do NOT pass `afterItemURN` (which would scope the result to "after the
  // current section") — omitting it lets the backend return the learner's true next item.
  const { data: nextItem, loading: nextItemLoading } = useLearningJourneyUpcomingItemQueryRemote({
    variables: {
      journeyId: Number(journeyId),
      spaceIds: [journey?.spaceId ?? 0],
      userId: user?.id ?? 0,
      includeContentProgress: true,
    },
    fetchPolicy: 'network-only',
    skip: !journeyId || !journey?.spaceId || !user?.id,
  });

  const { data: upcomingEventLocation, loading: upcomingEventLocationLoading } =
    useLearningJourneyUpcomingEventLocationQueryRemote({
      variables: {
        id: Number(journeyId),
        spaceIds: [journey?.spaceId ?? 0],
      },
      skip: !journeyId || !journey?.spaceId,
    });

  const {
    journeySession,
    loading: journeySessionLoading,
    error: journeySessionError,
  } = useLearningJourneySession({ id: Number(journeyId) });

  const {
    participation: journeyParticipation,
    loading: journeyParticipationLoading,
    error: journeyParticipationError,
    refetch: participationRefetch,
  } = useUserContentLastParticipation({
    userId: user?.id,
    contentURN: journey?.urn,
  });

  const learners =
    journeySession?.cohort.learners.map((learner) => ({
      id: learner.user.id,
      email: learner.user.email || '',
      profileImage: learner.user.profileImage || '',
      name: learner.user.username || '',
    })) || [];

  const [joinJourney, { loading: onJoinJourneyLoading }] = useJoinLearningJourneyCohortsMutationRemote({
    refetchQueries: ['learningJourneySession'],
  });

  const { isSaved: isSavedFavourite, handleSavedLearnings } = useFavouriteJourney(journey?.id);

  const openShowDescriptionModal = () => {
    setModal(ModalType.ShowDescriptionModal);
  };

  const handleModalClose = () => {
    setModal(null);
  };

  const handleLearnersViewAll = () => {
    if (!journey || !journeySession) return;

    navigate(`${ROUTE_PATH.JOURNEY_BASE}/${journey.id}/${slugify(journey.title)}/sessions/${journeySession?.id}/participants`);
  };

  const onJoinJourney = async () => {
    if (!user?.id || !journeySession?.id) return;

    await joinJourney({ variables: { userId: user.id, sessionId: journeySession.id } });

    participationRefetch();

    handleModalClose();
  };

  const onPreJoinItemClick = () => setModal(ModalType.JoinJourneyModal);

  if (journeyLoading || journeySessionLoading || userLoading) return <JourneyViewLayout.LoadingState />;

  if (journeyError || journeySessionError || journeyError || journeyParticipationError) {
    navigate404();
    return null;
  }

  if (!journey) return null;
  const sortedItems = items?.slice()?.sort((a, b) => a.order - b.order);
  const allUpcomingItemsUnlockAtFromJourney =
    sortedItems?.find((item) => item.isLocked && !item.isOptional && item.availabilityScheduledAt)?.availabilityScheduledAt ||
    sortedItems?.find((item) => item.isLocked && item.availabilityScheduledAt)?.availabilityScheduledAt;

  const sectionItemsData = learningJourneySection?.learningV2?.sectionItems?.data;
  const sortedSectionItems = sectionItemsData?.slice()?.sort((a, b) => a.order - b.order);
  const firstIncompleteRequiredSectionItem = sortedSectionItems
    ?.filter((item) => !item.isOptional)
    .find((item) => !item.learningItem?.progress?.isCompleted);

  const allUpcomingItemsUnlockAtFromSection =
    firstIncompleteRequiredSectionItem?.isLocked && firstIncompleteRequiredSectionItem.availabilityScheduledAt
      ? convertAvailabilityScheduledAtWithTimezone({
          availabilityScheduledAt: firstIncompleteRequiredSectionItem.availabilityScheduledAt,
          availabilityScheduledAtTimezone: firstIncompleteRequiredSectionItem.availabilityScheduledAtTimezone,
          direction: 'fromCustomTimezone',
        })
      : undefined;

  const allUpcomingItemsUnlockAt =
    sectionId && sectionItemsData?.length
      ? (allUpcomingItemsUnlockAtFromSection ?? allUpcomingItemsUnlockAtFromJourney)
      : allUpcomingItemsUnlockAtFromJourney;

  const upcomingJourneyItem = nextItem?.learningJourneyUpcomingItem;
  const nextRequiredUpcomingIsLocked = Boolean(upcomingJourneyItem?.isLocked) && !upcomingJourneyItem?.isOptional;
  const sectionAllUpcomingLocked = Boolean(sectionId) && Boolean(firstIncompleteRequiredSectionItem?.isLocked);

  const allUpcomingItemsLocked =
    Boolean(journey.allUpcomingItemsLocked) || nextRequiredUpcomingIsLocked || sectionAllUpcomingLocked;

  const certificateEnabled = Boolean(journey.certificateEnabled);
  const journeyCompleted = Boolean(journeyParticipation?.isCompleted);
  const certificateReady = Boolean(journey.myCertificateURL?.url);

  const showCertificateBanner = certificateEnabled && journeyCompleted && certificateReady;

  const handleSeeCertificate = () => {
    navigate(`${ROUTE_PATH.JOURNEY_BASE}/${journey.id}/${slugify(journey.title)}/certificate`);
  };

  // The V2 JSON fields are the source of truth. Resolve them into plain strings here
  // so the layout and modal stay format-agnostic.
  const journeyDescription = richTextJsonToString(journey.descriptionV2);
  const journeyBusinessGoal = richTextJsonToString(journey.businessGoalV2);

  return (
    <>
      <JourneyJoinModal
        open={activeModal === ModalType.JoinJourneyModal}
        onClose={handleModalClose}
        onJoin={onJoinJourney}
        loading={onJoinJourneyLoading}
      />
      <JourneyViewShowDescriptionModal
        open={activeModal === ModalType.ShowDescriptionModal}
        onClose={handleModalClose}
        description={journeyDescription}
        businessGoal={journeyBusinessGoal}
        spaceId={journey?.spaceId ?? 0}
      />
      <JourneyViewLayout
        journey={journey}
        description={journeyDescription}
        businessGoal={journeyBusinessGoal}
        learnersPage={learners}
        learnersTotal={learners.length}
        onLearnersViewAll={handleLearnersViewAll}
        // @ts-expect-error Generated type for availabilityScheduledAt is weird (it should be string | undefined, but is generated as Date)
        learningItems={
          items
            ?.slice() // clone before sort (avoid mutation of original array)
            ?.sort((a, b) => a.order - b.order)
            ?.map((item, order) => ({
              ...learningContentDisplayMeta(item.content, journey.spaceId, journey),
              objectId: item.contentId,
              locked: item.isLocked,
              type: item.content?.metadata?.type,
              level: item.content?.metadata?.level,
              childrenCount: item?.content?.plannedLearningsCount,
              disabled: item.isLocked,
              totalEffort: item.content?.plannedDuration,
              orderIndex: order + 1,
              description: item.content?.description,
              locations: getAssignedSessionLocations(
                item.content?.locations,
                (item.content as any)?.locationsWithEnrollment as
                  | Array<{ id?: number; userLocationEnrollment?: Array<{ id?: number }> }>
                  | undefined,
              ),
              isOptional: item.isOptional,
              availabilityScheduledAt: convertAvailabilityScheduledAtWithTimezone({
                availabilityScheduledAt: item.availabilityScheduledAt,
                availabilityScheduledAtTimezone: item.availabilityScheduledAtTimezone,
                direction: 'fromCustomTimezone',
              }),
              availabilityScheduledAtTimezone: item.availabilityScheduledAtTimezone,
              provider: item.content?.provider,
              progress: item.content?.progress,
              error: !item.content ? LearningCardError.UNAVAILABLE : undefined,
            })) || []
        }
        section={
          learningJourneySection?.learningV2
            ? {
                id: learningJourneySection.learningV2.id,
                title: learningJourneySection.learningV2.title,
                description: learningJourneySection.learningV2.teaser,
                childrenCount: learningJourneySection.learningV2?.sectionItems?.data?.length,
              }
            : undefined
        }
        sectionLoading={learningJourneySectionLoading}
        sectionLearningItems={
          learningJourneySection?.learningV2?.sectionItems?.data
            ?.slice() // clone before sort (avoid mutation of original array)
            ?.sort((a, b) => a.order - b.order)
            ?.map((item, order) => ({
              ...learningContentDisplayMeta(item.learningItem, journey.spaceId, journey),
              objectId: item.learningItemURNV2,
              locked: item.isLocked,
              type: item.learningItem?.metadata?.type,
              level: item.learningItem?.metadata?.level,
              childrenCount: item?.learningItem?.plannedLearningsCount,
              disabled: item.isLocked,
              totalEffort: item.learningItem?.plannedDuration,
              orderIndex: order + 1,
              description: item.learningItem?.description,
              locations: getAssignedSessionLocations(
                item.learningItem?.locations,
                (item.learningItem as any)?.locationsWithEnrollment as
                  | Array<{ id?: number; userLocationEnrollment?: Array<{ id?: number }> }>
                  | undefined,
              ),
              isOptional: item.isOptional,
              availabilityScheduledAt: convertAvailabilityScheduledAtWithTimezone({
                availabilityScheduledAt: item.availabilityScheduledAt,
                availabilityScheduledAtTimezone: item.availabilityScheduledAtTimezone,
                direction: 'fromCustomTimezone',
              }) as unknown as Date,
              availabilityScheduledAtTimezone: item.availabilityScheduledAtTimezone,
              provider: item.learningItem?.provider,
              progress: item.learningItem?.progress,
              error: !item.learningItem ? LearningCardError.UNAVAILABLE : undefined,
            })) || []
        }
        upcomingEventItem={
          upcomingEventLocation?.learningJourneyUpcomingEventLocation
            ? {
                ...learningContentDisplayMeta(
                  upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent,
                  journey.spaceId,
                  journey,
                ),
                objectId: upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent?.id || '',
                locked: false,
                type: upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent?.metadata?.type,
                level: upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent?.metadata?.level,
                disabled: false,
                totalEffort: upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent?.plannedDuration,
                description: upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent?.description,
                locations: [upcomingEventLocation.learningJourneyUpcomingEventLocation],
                isOptional: false,
                availabilityScheduledAt: undefined,
                availabilityScheduledAtTimezone: undefined,
                provider: upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent?.provider,
                progress: undefined,
                error: !upcomingEventLocation.learningJourneyUpcomingEventLocation?.learningContent
                  ? LearningCardError.UNAVAILABLE
                  : undefined,
              }
            : undefined
        }
        nextLearningItem={
          nextItem?.learningJourneyUpcomingItem
            ? {
                ...learningContentDisplayMeta(nextItem.learningJourneyUpcomingItem?.content, journey.spaceId, journey),
                objectId: nextItem?.learningJourneyUpcomingItem?.content?.id || '',
                locked: nextItem?.learningJourneyUpcomingItem?.isLocked,
                type: nextItem?.learningJourneyUpcomingItem?.content?.metadata?.type,
                level: nextItem?.learningJourneyUpcomingItem?.content?.metadata?.level,
                disabled: false,
                totalEffort: nextItem?.learningJourneyUpcomingItem?.content?.plannedDuration,
                description: nextItem?.learningJourneyUpcomingItem?.content?.description,
                locations: getAssignedSessionLocations(
                  nextItem?.learningJourneyUpcomingItem?.content?.locations,
                  (nextItem?.learningJourneyUpcomingItem?.content as any)?.locationsWithEnrollment as
                    | Array<{ id?: number; userLocationEnrollment?: Array<{ id?: number }> }>
                    | undefined,
                ),
                isOptional: nextItem?.learningJourneyUpcomingItem?.isOptional,
                availabilityType: nextItem?.learningJourneyUpcomingItem?.availabilityType,
                availabilityScheduledAt: nextItem?.learningJourneyUpcomingItem?.availabilityScheduledAt,
                availabilityScheduledAtTimezone: nextItem?.learningJourneyUpcomingItem?.availabilityScheduledAtTimezone,
                provider: nextItem?.learningJourneyUpcomingItem?.content?.provider,
                progress: nextItem?.learningJourneyUpcomingItem?.content?.progress,
                error: !nextItem?.learningJourneyUpcomingItem ? LearningCardError.UNAVAILABLE : undefined,
              }
            : undefined
        }
        nextLearningItemLoading={nextItemLoading}
        upcomingEventItemLoading={upcomingEventLocationLoading || journeyLoading}
        learningItemsTotal={items?.filter((item) => !item.isOptional)?.length || 0}
        onExpandDescription={openShowDescriptionModal}
        isSavedFavourite={isSavedFavourite}
        onSaveFavourite={handleSavedLearnings}
        journeyParticipation={journeyParticipation}
        journeyParticipationLoading={journeyParticipationLoading}
        onJoinJourneyLoading={onJoinJourneyLoading}
        onJoinJourney={onJoinJourney}
        isInPreviewModal={isInPreviewModal}
        onPreJoinItemClick={onPreJoinItemClick}
        allUpcomingItemsLocked={allUpcomingItemsLocked}
        allUpcomingItemsUnlockAt={allUpcomingItemsUnlockAt}
        showCertificateBanner={showCertificateBanner}
        learnerFirstName={user?.firstname}
        onSeeCertificate={handleSeeCertificate}
      />
    </>
  );
};
